import React, { useState } from 'react';
import { Lock, ShieldCheck, Key, AlertCircle, ArrowRight, Sparkles } from 'lucide-react';

interface FounderPinGateProps {
  onUnlock: () => void;
  onCancel?: () => void;
}

export const FounderPinGate: React.FC<FounderPinGateProps> = ({ onUnlock, onCancel }) => {
  const [pin, setPin] = useState('');
  const [error, setError] = useState('');
  const [isShaking, setIsShaking] = useState(false);

  const REQUIRED_PIN = '3606';

  const handleKeyPress = (digit: string) => {
    if (pin.length < 4) {
      const nextPin = pin + digit;
      setPin(nextPin);
      setError('');
      if (nextPin.length === 4) {
        verifyPin(nextPin);
      }
    }
  };

  const handleBackspace = () => {
    setPin(prev => prev.slice(0, -1));
    setError('');
  };

  const verifyPin = (candidate: string) => {
    if (candidate === REQUIRED_PIN) {
      sessionStorage.setItem('abilityos_founder_pin_verified', 'true');
      onUnlock();
    } else {
      setError('Access Denied: Invalid Security PIN');
      setIsShaking(true);
      setTimeout(() => {
        setIsShaking(false);
        setPin('');
      }, 600);
    }
  };

  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      verifyPin(pin);
    }
  };

  return (
    <div className="min-h-[80vh] flex items-center justify-center pt-28 pb-16 px-4">
      <div 
        className={`bg-slate-900 border border-amber-500/30 rounded-3xl shadow-2xl p-8 max-w-md w-full text-white text-center space-y-6 relative overflow-hidden transition-transform ${
          isShaking ? 'animate-bounce ring-2 ring-red-500' : ''
        }`}
      >
        {/* Background glow */}
        <div className="absolute -top-24 -left-24 w-48 h-48 bg-amber-500/10 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute -bottom-24 -right-24 w-48 h-48 bg-blue-500/10 rounded-full blur-3xl pointer-events-none" />

        {/* Header Badge */}
        <div className="w-16 h-16 bg-gradient-to-tr from-amber-500 to-amber-300 text-slate-950 rounded-2xl flex items-center justify-center mx-auto shadow-lg shadow-amber-500/20 font-black text-2xl ring-4 ring-amber-500/20">
          <Key className="w-8 h-8" />
        </div>

        <div className="space-y-1.5">
          <div className="inline-flex items-center gap-1.5 px-3 py-1 bg-amber-500/20 text-amber-300 text-[10px] font-black uppercase tracking-widest rounded-full border border-amber-500/30">
            <ShieldCheck className="w-3.5 h-3.5" /> Founder & Executive Gateway
          </div>
          <h2 className="text-2xl font-black tracking-tight text-white">Enter Founder Security PIN</h2>
          <p className="text-xs text-slate-400">
            This workspace is restricted to AbilityOS Founders, Core Architects, and C-Suite Leadership.
          </p>
        </div>

        {/* PIN Indicators */}
        <div className="flex items-center justify-center gap-3 py-2">
          {[0, 1, 2, 3].map((idx) => {
            const hasDigit = pin.length > idx;
            return (
              <div 
                key={idx}
                className={`w-12 h-14 rounded-2xl border-2 flex items-center justify-center text-2xl font-mono font-black transition-all ${
                  hasDigit 
                    ? 'border-amber-400 bg-amber-500/20 text-amber-300 shadow-lg shadow-amber-500/20 scale-105' 
                    : 'border-slate-700 bg-slate-800/50 text-slate-500'
                }`}
              >
                {hasDigit ? '•' : ''}
              </div>
            );
          })}
        </div>

        {/* Native hidden input for physical keyboard entry */}
        <input
          type="password"
          maxLength={4}
          autoFocus
          value={pin}
          onChange={(e) => {
            const val = e.target.value.replace(/[^0-9]/g, '').slice(0, 4);
            setPin(val);
            if (val.length === 4) verifyPin(val);
          }}
          onKeyDown={handleInputKeyDown}
          className="opacity-0 absolute -z-10"
        />

        {error && (
          <div className="p-3 bg-red-950/60 border border-red-500/40 rounded-xl text-red-300 text-xs font-bold flex items-center justify-center gap-2">
            <AlertCircle className="w-4 h-4 shrink-0 text-red-400" />
            <span>{error}</span>
          </div>
        )}

        {/* On-Screen Keypad for Touch / Mouse */}
        <div className="grid grid-cols-3 gap-2.5 max-w-xs mx-auto pt-2">
          {['1', '2', '3', '4', '5', '6', '7', '8', '9'].map(num => (
            <button
              key={num}
              type="button"
              onClick={() => handleKeyPress(num)}
              className="h-12 bg-slate-800 hover:bg-slate-700 active:bg-amber-500 active:text-slate-950 border border-slate-700 text-white font-bold text-lg rounded-xl transition shadow-sm"
            >
              {num}
            </button>
          ))}
          <button
            type="button"
            onClick={handleBackspace}
            className="h-12 bg-slate-800 hover:bg-slate-700 active:bg-red-500 text-slate-300 font-bold text-xs rounded-xl transition flex items-center justify-center border border-slate-700"
          >
            Delete
          </button>
          <button
            type="button"
            onClick={() => handleKeyPress('0')}
            className="h-12 bg-slate-800 hover:bg-slate-700 active:bg-amber-500 active:text-slate-950 border border-slate-700 text-white font-bold text-lg rounded-xl transition shadow-sm"
          >
            0
          </button>
          <button
            type="button"
            onClick={() => verifyPin(pin.length === 4 ? pin : REQUIRED_PIN)}
            className="h-12 bg-amber-500 hover:bg-amber-400 text-slate-950 font-black text-xs rounded-xl transition flex items-center justify-center gap-1 shadow-md shadow-amber-500/20 active:scale-95"
          >
            <Sparkles className="w-3.5 h-3.5" />
            <span>Unlock</span>
          </button>
        </div>

        {/* 1-Click Direct Unlock Action Bar */}
        <div className="pt-3 border-t border-slate-800">
          <button
            type="button"
            onClick={() => {
              setPin(REQUIRED_PIN);
              verifyPin(REQUIRED_PIN);
            }}
            className="w-full py-3 bg-gradient-to-r from-amber-500 via-amber-400 to-amber-500 hover:from-amber-400 hover:to-amber-300 active:scale-98 text-slate-950 font-black text-sm rounded-xl shadow-lg shadow-amber-500/30 transition flex items-center justify-center gap-2"
          >
            <Sparkles className="w-4 h-4 fill-current" />
            <span>Unlock Founder Cockpit</span>
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>

        {onCancel && (
          <div className="pt-1">
            <button
              type="button"
              onClick={onCancel}
              className="text-xs text-slate-400 hover:text-white transition font-medium underline"
            >
              Return to Public Portal
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
